import type { ContactEvent } from '../types.js';

export interface ContactDetectorConfig {
  /** Absolute minimum peak speed (image-units / sec) to count as contact. */
  readonly thresholdSpeed: number;
  /**
   * Minimum gap (ms) between two consecutive accepted peaks. Prevents the
   * same swing producing several "events" because of frame-to-frame jitter.
   */
  readonly refractoryPeriodMs: number;
  /** Frames per second of the input series. */
  readonly fps: number;
  /**
   * When true (default), adapt the threshold to the statistics of the input
   * series: a peak must clear both the absolute floor AND `mean + kSigma *
   * stdev` of the wrist speed. This gives meaningful recall on TV-broadcast
   * footage where absolute pixel speeds are tiny, without producing false
   * positives on static camera noise.
   */
  readonly adaptive: boolean;
  /** Number of stdevs above the mean for the adaptive threshold. */
  readonly kSigma: number;
}

const DEFAULTS = {
  // Absolute floor — 0.8 image-units/sec picks up even distant broadcast
  // footage where the player occupies ~10% of frame height. Close-up recording
  // easily clears any sensible adaptive threshold built on top of this.
  thresholdSpeed: 0.8,
  refractoryPeriodMs: 600, // a tennis stroke takes ~0.4–0.8s, refractory 0.6s
  adaptive: true,
  kSigma: 2.0,
} as const;

function computeAdaptiveThreshold(series: readonly number[], kSigma: number): number {
  const n = series.length;
  if (n === 0) return 0;
  let mean = 0;
  for (const v of series) mean += v;
  mean /= n;
  let variance = 0;
  for (const v of series) {
    const d = v - mean;
    variance += d * d;
  }
  variance /= n;
  const stdev = Math.sqrt(variance);
  return mean + kSigma * stdev;
}

/**
 * Detect contact events as local maxima of `wristSpeed` above a threshold,
 * separated by at least `refractoryPeriodMs`. Confidence is a normalized
 * ratio of peak magnitude over threshold (capped to 1).
 *
 * The threshold is `max(thresholdSpeed, mean + kSigma*stdev)` when
 * `adaptive` is enabled — this lets the same defaults work for both
 * close-up phone footage (high absolute speeds, adaptive dominates) and
 * distant broadcast footage (low speeds, absolute floor dominates).
 */
export function detectContacts(
  wristSpeed: readonly number[],
  config: Partial<ContactDetectorConfig> & Pick<ContactDetectorConfig, 'fps'>,
): ContactEvent[] {
  const { fps } = config;
  const absoluteFloor = config.thresholdSpeed ?? DEFAULTS.thresholdSpeed;
  const refractoryPeriodMs = config.refractoryPeriodMs ?? DEFAULTS.refractoryPeriodMs;
  const adaptive = config.adaptive ?? DEFAULTS.adaptive;
  const kSigma = config.kSigma ?? DEFAULTS.kSigma;
  if (fps <= 0 || wristSpeed.length < 3) return [];

  const adaptiveThreshold = adaptive ? computeAdaptiveThreshold(wristSpeed, kSigma) : 0;
  const threshold = Math.max(absoluteFloor, adaptiveThreshold);

  const refractoryFrames = Math.max(1, Math.round((refractoryPeriodMs / 1000) * fps));
  const events: ContactEvent[] = [];
  let lastAcceptedFrame = -refractoryFrames;

  for (let i = 1; i < wristSpeed.length - 1; i++) {
    const prev = wristSpeed[i - 1] ?? 0;
    const here = wristSpeed[i] ?? 0;
    const next = wristSpeed[i + 1] ?? 0;

    const isLocalMax = here > prev && here >= next;
    if (!isLocalMax) continue;
    if (here < threshold) continue;
    if (i - lastAcceptedFrame < refractoryFrames) {
      // Within refractory: replace the previous event if this peak is larger.
      const last = events[events.length - 1];
      if (last && here > last.peakAngularVelocity) {
        events[events.length - 1] = {
          frameIndex: i,
          timeMs: (i / fps) * 1000,
          peakAngularVelocity: here,
          confidence: Math.min(1, here / threshold - 1 + 0.5),
        };
        lastAcceptedFrame = i;
      }
      continue;
    }

    events.push({
      frameIndex: i,
      timeMs: (i / fps) * 1000,
      peakAngularVelocity: here,
      confidence: Math.min(1, here / threshold - 1 + 0.5),
    });
    lastAcceptedFrame = i;
  }

  return events;
}
